import React from 'react';
import {
  Card,
  Pagination,
  PageSection,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Label,
  PaginationVariant
} from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { accountsRows, accountsColumns } from '@app/Findings/sampleData';
import { ChartDonut } from '@patternfly/react-charts';
import { PRODUCT_NAME } from 'src/config/partner';

export const accounts: React.FunctionComponent = () => {
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [paginatedRows, setPaginatedRows] = React.useState(accountsRows.slice(0, 10));

  const handleSetPage = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number,
    _perPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(accountsRows.slice(startIdx, endIdx));
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(accountsRows.slice(startIdx, endIdx));
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={accountsRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const tableToolbar = (
    <Toolbar usePageInsets id="compact-toolbar">
      <ToolbarContent>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'TRUE':
        return <Label color="green">{labelText}</Label>;
      case 'FALSE':
        return <Label color="red">{labelText}</Label>;
      default:
        return <></>;
    }
  };

  const renderPriority = (priority: number) => {
    if (priority > 8) {
        return <Label color="red">{priority}</Label>;
    } else if (priority > 5) {
        return <Label color="orange">{priority}</Label>;
    } else {
        return <Label color="green">{priority}</Label>;
    }
  };

  const trueCount = accountsRows.filter((obj) => obj.MFAStatus === 'TRUE').length;
  const falseCount = accountsRows.filter((obj) => obj.MFAStatus === 'FALSE').length;

  return (
    <React.Fragment>
        <PageSection isFilled>
          <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Active Accounts</Title>
          <br />
          <Card>
            {tableToolbar}
            <Table variant="compact" aria-label="Compact Table">
              <Thead>
                <Tr>
                  <Th key={0}>{accountsColumns[0]}</Th>
                  <Th key={1}>{accountsColumns[1]}</Th>
                  <Th key={2}>{accountsColumns[2]}</Th>
                  <Th key={3}>{accountsColumns[3]}</Th>
                  <Th key={4}>{accountsColumns[4]}</Th>
                  <Th key={5}>{accountsColumns[5]}</Th>
                  <Th key={6}>{accountsColumns[6]}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <>
                      <Td dataLabel={accountsColumns[0]}>{row.Source}</Td>
                      <Td dataLabel={accountsColumns[1]}>{row.Firstname}</Td>
                      <Td dataLabel={accountsColumns[2]}>{row.Lastname}</Td>
                      <Td dataLabel={accountsColumns[3]}>{row.Email}</Td>
                      <Td dataLabel={accountsColumns[4]}>{row.LastActivity}</Td>
                      <Td dataLabel={accountsColumns[5]}>{renderLabel(row.MFAStatus)}</Td>
                      <Td dataLabel={accountsColumns[6]}>{row.PrivilegedRoles}</Td>
                    </>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
        </PageSection>

        <div style={{ height: '230px', width: '450px' }}>
          <ChartDonut
            ariaDesc="Account MFA Compliance"
            ariaTitle="Account MFA Compliance"
            data={[{ x: 'MFA Disabled', y: falseCount }, { x: 'MFA Enabled', y: trueCount }]}
            labels={({ datum }) => `${datum.x}: ${datum.y}`}
            style={{
                data: {
                    fill: ({ datum }) => {
                        if (datum.x === 'MFA Disabled') {
                            return 'red';
                        } else if (datum.x === 'MFA Enabled') {
                            return 'green';
                        }
                        return null;
                    }
                }
            }}
            legendData={[{ name: 'MFA Disabled: '+falseCount, symbol: { fill: 'red' }}, { name: 'MFA Enabled: '+trueCount, symbol: { fill: 'green' }}]}
            legendOrientation="vertical"
            legendPosition="right"
            name="chart1"
             padding={{
               bottom: 20,
               left: 50,
               right: 200, // Adjusted to accommodate legend
               top: 20
             }}
            title={accountsRows.length}
            subTitle="Active Accounts"
            width={450}
            innerRadius={80}
            constrainToVisibleArea
          />
        </div>
    </React.Fragment>
  );
};
